import { useDialog } from '@/common/dialog/DialogContext';
import { EntitySelectDialog } from '@/common/dialog/EntitySelectDialog';
import { useTab } from '@/common/entityFramework/entityTabs/TabContext';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useForm } from '@/common/form/FormContext';
import { useFormField } from '@/common/form/hooks/useFormField';
import { useUnqCode } from '@/common/form/hooks/useUnqCode';
import { useNavState } from '@/common/navState/NavStateContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, IconButton, InputAdornment, TextField } from '@mui/material';
import { useEffect } from 'react';

export const Ndc11CodeFormField = ({ fieldDef }) => {
  const { tabLabel } = useTab();
  const { entityFormMode } = useEntityMgr();
  const { navigateTo } = useNavState();
  const { openDialog } = useDialog();
  const { formValues, assignValue } = useForm();
  const { onBlur, error, helperText, variant } = useFormField(
    'code',
    ['code', 'ndc9ProdId'],
    () => {
      if (!formValues.ndc9ProdId) {
        return 'A NDC9 Product must be selected';
      } else if (formValues.code?.length < 11) {
        return 'An NDC11 code must consist of 11 numbers';
      }
    },
    'NDC11 product code',
  );

  // When not adding, the field is disabled
  const disabled = entityFormMode !== EntityFormMode.ADD;
  const showNavigateButton = tabLabel !== 'Info';

  useEffect(() => {
    if (!formValues.ndc9ProdId || disabled) return;
    assignValue('code', formValues.ndc9ProdCode);
  }, [formValues.ndc9ProdId]);

  const handleClick = () => {
    if (disabled) {
      navigateToEntity();
      return;
    }
    openEntitySelectDialog();
  };

  const navigateToEntity = () => {
    const route = 'md/ndc11-prod';
    const navState = {
      entityId: formValues.id,
      mode: Mode.DETAIL,
    };
    navigateTo(route, navState);
  };

  const openEntitySelectDialog = () => {
    openDialog(EntitySelectDialog, {
      data: {
        module: 'md',
        entity: 'NDC9_PROD',
        rowSelection: 'SINGLE',
      },
    }).then((selectedEntities) => {
      onBlur();
      if (!selectedEntities || selectedEntities.length === 0) return;
      const labeler = selectedEntities[0];
      // Save the labeler code so we know the fixed 5 digits.
      assignValue('ndc9ProdId', labeler.id);
      assignValue('ndc9ProdCode', labeler.code);
    });
  };

  // Handle user input.
  const handleChange = (event) => {
    const { value } = event.target;
    // If the hyphen was deleted, ignore this input.
    if (!value.includes('-')) return;

    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '');

    // The first 5 digits are fixed from the labeler. We assume these exist.
    const ndc9ProdCode = formValues.ndc9ProdCode || digits.slice(0, 9);
    // Force the first 5 digits to be the labeler code.
    digits = ndc9ProdCode + digits.slice(ndc9ProdCode.length);

    // Only allow up to 11 total digits (5 fixed + up to 4 additional)
    if (digits.length > 11) {
      digits = digits.slice(0, 11);
    }
    assignValue('code', digits);
  };

  const { unqCodeErrorState } = useUnqCode(disabled, formValues.code);

  // Format the value for display: insert a hyphen after the first 5 digits.
  const inputText = () => {
    if (!formValues.code) return '';
    const code = formValues.code;
    const labelerPart = code.slice(0, 5);
    const ndc9Part = code.slice(5, 9);
    const remainder = code.slice(9);
    return labelerPart + '-' + ndc9Part + '-' + remainder;
  };

  const icon = () => {
    return disabled ? fas.faLink : fas.faPills;
  };

  return (
    <TextField
      variant={variant}
      error={unqCodeErrorState || error}
      disabled={disabled}
      fullWidth
      helperText={unqCodeErrorState ?? helperText}
      label={fieldDef.displayName}
      value={inputText()}
      onChange={handleChange}
      onBlur={onBlur}
      slotProps={{
        input: {
          endAdornment: (showNavigateButton || !disabled) && (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={(event) => handleClick(event)}>
                <Icon>
                  <FontAwesomeIcon style={{ width: '100%' }} icon={icon()} />
                </Icon>
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
